import * as THREE from 'three';
import { MeshBVH, NOT_INTERSECTED, INTERSECTED } from 'three-mesh-bvh';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { CollisionOptions, ICollisionSystem } from './colliderTypes';

interface MeshBVHCollisionConfig {
  cameraRadius: number;
  enabled: boolean;
  epsilon: number;
  maxPushIterations: number;
}

const DEFAULT_CONFIG: MeshBVHCollisionConfig = {
  cameraRadius: 0.05,
  enabled: true,
  epsilon: 0.0001,
  maxPushIterations: 5,
};

/**
 * Build a merged collision geometry from a set of Object3D roots.
 * Bakes world transforms into vertex positions and strips everything
 * except position + index for minimal memory.
 *
 * Returns the merged geometry with a `boundsTree` attached, or `null`
 * if no meshes were found.
 */
export const buildCollisionBVH = (
  roots: Iterable<THREE.Object3D>,
): { geometry: THREE.BufferGeometry; bvh: MeshBVH } | null => {
  const geometries: THREE.BufferGeometry[] = [];

  for (const root of roots) {
    root.updateWorldMatrix(true, true);
    root.traverse((child) => {
      if (!(child as THREE.Mesh).isMesh) return;
      const mesh = child as THREE.Mesh;
      if (!mesh.geometry) return;

      const srcGeom = mesh.geometry;
      const posAttr = srcGeom.getAttribute('position');
      if (!posAttr) return;

      // Create a stripped geometry with only position + index
      const stripped = new THREE.BufferGeometry();
      stripped.setAttribute('position', posAttr.clone());
      if (srcGeom.index) {
        stripped.setIndex(srcGeom.index.clone());
      }

      // Bake the world transform into vertex positions
      stripped.applyMatrix4(mesh.matrixWorld);
      geometries.push(stripped);
    });
  }

  if (geometries.length === 0) return null;

  const merged = mergeGeometries(geometries, false);
  if (!merged) {
    for (const g of geometries) g.dispose();
    return null;
  }

  // Dispose cloned intermediates
  for (const g of geometries) g.dispose();

  const bvh = new MeshBVH(merged, { maxLeafTris: 5 });
  merged.boundsTree = bvh;

  return { geometry: merged, bvh };
};

export class MeshBVHCollisionSystem implements ICollisionSystem {
  private bvh: MeshBVH | null;
  private config: MeshBVHCollisionConfig;

  // Scratch objects — zero-allocation hot path
  private readonly _box = new THREE.Box3();
  private readonly _triPoint = new THREE.Vector3();
  private readonly _center = new THREE.Vector3();
  private readonly _pushDir = new THREE.Vector3();
  private readonly _rayDir = new THREE.Vector3();
  private readonly _ray = new THREE.Ray();

  constructor(bvh: MeshBVH, config?: Partial<MeshBVHCollisionConfig>) {
    this.bvh = bvh;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  getSafePosition(
    currentPosition: THREE.Vector3,
    desiredPosition: THREE.Vector3,
    _options?: CollisionOptions,
  ): THREE.Vector3 {
    if (!this.config.enabled || !this.bvh) {
      return desiredPosition;
    }

    const radius = this.config.cameraRadius;
    const epsilon = this.config.epsilon;
    const center = this._center.copy(desiredPosition);
    const box = this._box;
    const triPoint = this._triPoint;
    const pushDir = this._pushDir;
    const bvh = this.bvh;

    let anyCollided = false;

    for (let iter = 0; iter < this.config.maxPushIterations; iter++) {
      let iterPushSq = 0;

      box.min.set(
        center.x - radius,
        center.y - radius,
        center.z - radius,
      );
      box.max.set(
        center.x + radius,
        center.y + radius,
        center.z + radius,
      );

      bvh.shapecast({
        intersectsBounds: (nodeBox: THREE.Box3) => {
          return nodeBox.intersectsBox(box) ? INTERSECTED : NOT_INTERSECTED;
        },
        intersectsTriangle: (tri: THREE.Triangle) => {
          tri.closestPointToPoint(center, triPoint);
          const distSq = center.distanceToSquared(triPoint);

          if (distSq < radius * radius) {
            const dist = Math.sqrt(distSq);
            const depth = radius - dist;

            if (dist > 1e-10) {
              pushDir.subVectors(center, triPoint).normalize();
            } else {
              // Degenerate: camera center is exactly on triangle — use triangle normal
              tri.getNormal(pushDir);
            }

            center.addScaledVector(pushDir, depth + 1e-5);
            iterPushSq += depth * depth;
            anyCollided = true;
          }

          return false; // continue traversal
        },
      });

      if (iterPushSq < epsilon * epsilon) break;
    }

    if (!anyCollided) {
      return desiredPosition;
    }

    return center.clone();
  }

  isLineOfSightObstructed(
    start: THREE.Vector3,
    target: THREE.Vector3,
    padding: number = this.config.cameraRadius,
  ): boolean {
    if (!this.config.enabled || !this.bvh) {
      return false;
    }

    const dir = this._rayDir.subVectors(target, start);
    const totalDist = dir.length();

    if (totalDist <= padding) {
      return false;
    }

    dir.normalize();
    const ray = this._ray;
    ray.set(start, dir);

    const checkDist = totalDist - padding;
    const hits = this.bvh.raycast(ray, THREE.DoubleSide);

    for (let i = 0; i < hits.length; i++) {
      if (hits[i].distance <= checkDist) {
        return true;
      }
    }

    return false;
  }

  updateConfig(patch: Partial<MeshBVHCollisionConfig>): void {
    this.config = { ...this.config, ...patch };
  }

  getConfig(): { epsilon: number; enabled: boolean; cameraRadius: number } {
    return {
      epsilon: this.config.epsilon,
      enabled: this.config.enabled,
      cameraRadius: this.config.cameraRadius,
    };
  }

  dispose(): void {
    this.bvh = null;
  }
}
